// src/modules/orders/OrderTracking.jsx
import { useState, useEffect, useMemo } from "react";
import { C } from "../../constants";
import { apiCall } from "../../api";
import OrderDetail from "./OrderDetail";
import QuoteDetail from "../quotes/QuoteDetail";

const STATUS_COLORS = {
  "Received":  { bg: "#DBEAFE", c: "#1E40AF" },
  "Approved":  { bg: "#D1FAE5", c: "#065F46" },
  "Paused":    { bg: "#FEF3C7", c: "#92400E" },
  "Rejected":  { bg: "#FEE2E2", c: "#991B1B" },
  "Processed": { bg: "#EDE9FE", c: "#5B21B6" },
  "Delivered": { bg: "#D1FAE5", c: "#065F46" },
};
const STATUS_OPTIONS = ["Received", "Approved", "Paused", "Rejected", "Processed", "Delivered"];

function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

function StatusPill({ status, orderId, isAdmin, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const s = STATUS_COLORS[status] || STATUS_COLORS["Received"];

  const handleSelect = async (newStatus) => {
    setOpen(false);
    if (newStatus === status || !isAdmin) return;
    setError("");
    try {
      const r = await apiCall("updateOrderStatus", { orderId, status: newStatus });
      if (r.success) {
        onStatusChange(orderId, newStatus);
      } else {
        setError(r.error || "Update failed.");
        setTimeout(() => setError(""), 4000);
      }
    } catch (err) { setError("Update failed."); setTimeout(() => setError(""), 4000); }
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={e => { e.stopPropagation(); if (isAdmin) setOpen(o => !o); }}
        style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
          cursor: isAdmin ? "pointer" : "default", border: "none", background: s.bg, color: s.c, fontFamily: "inherit",
          display: "flex", alignItems: "center", gap: 4 }}>
        {status} {isAdmin && <span style={{ fontSize: 8 }}>▾</span>}
      </button>
      {error && <div style={{ position: "absolute", top: "110%", left: 0, zIndex: 200, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: C.red, width: 260, whiteSpace: "normal" }}>{error}</div>}
      {open && isAdmin && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <div style={{ position: "absolute", top: "110%", left: 0, zIndex: 100, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: 160, overflow: "hidden" }}>
            {STATUS_OPTIONS.map(opt => {
              const sc = STATUS_COLORS[opt];
              return (
                <button key={opt} onClick={() => handleSelect(opt)}
                  style={{ display: "block", width: "100%", padding: "9px 14px", textAlign: "left", border: "none", background: opt === status ? C.grayBg : C.white, cursor: "pointer", fontSize: 13, color: C.t1, fontFamily: "inherit" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
                  onMouseLeave={e => e.currentTarget.style.background = opt === status ? C.grayBg : C.white}>
                  <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.c, marginRight: 8 }}>{opt}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function OrderTracking({ user }) {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [filterBy, setFilterBy]   = useState("");
  const [filterField, setFilterField] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [linkedQuote, setLinkedQuote] = useState(null);

  const isAdmin = (user?.role || "").toUpperCase() === "ADMIN";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const r = await apiCall("getOrders", { email: user.email, company: user.company, role: user.role || "CLIENT" });
      if (r.success) setOrders(r.orders || []);
    } catch (err) { console.error("Failed to load orders:", err); }
    setLoading(false);
  };

  const handleStatusChange = (orderId, newStatus) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    if (selected?.id === orderId) setSelected(prev => ({ ...prev, status: newStatus }));
  };

  const handleOpenQuote = async (quoteId, quoteNumber) => {
    if (!quoteId && !quoteNumber) return;
    try {
      const r = await apiCall("getQuotes", { email: user.email, company: user.company, role: user.role || "CLIENT" });
      if (r.success) {
        let q = null;
        if (quoteId) q = r.quotes.find(qt => qt.id === quoteId);
        if (!q && quoteNumber) q = r.quotes.find(qt => qt.quoteNumber === quoteNumber);
        if (q) setLinkedQuote(q);
      }
    } catch (err) { console.error("Failed to load quote:", err); }
  };

  const fmt = val => val ? "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—";

  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (filterStatus !== "all" && o.status !== filterStatus) return false;
      if (!filterBy.trim()) return true;
      const term = filterBy.toLowerCase();
      if (filterField === "company")      return (o.company     || "").toLowerCase().includes(term);
      if (filterField === "manufacturer") return (o.manufacturer|| "").toLowerCase().includes(term);
      if (filterField === "quoteNumber")  return (o.quoteNumber || "").toLowerCase().includes(term);
      if (filterField === "poNumber")     return (o.poNumber    || "").toLowerCase().includes(term);
      return (o.id          || "").toLowerCase().includes(term)
          || (o.quoteNumber || "").toLowerCase().includes(term)
          || (o.company     || "").toLowerCase().includes(term)
          || (o.manufacturer|| "").toLowerCase().includes(term)
          || (o.poNumber    || "").toLowerCase().includes(term)
          || (o.requestedBy || "").toLowerCase().includes(term);
    });
  }, [orders, filterBy, filterField, filterStatus]);

  return (
    <div style={{ width: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 4px" }}>Order Status & Tracking</h1>
          <p style={{ fontSize: 14, color: C.t2, margin: 0 }}>Track and manage all orders in one place.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <select value={filterField} onChange={e => setFilterField(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Fields</option>
            <option value="quoteNumber">Quote #</option>
            <option value="poNumber">PO Number</option>
            <option value="company">Company</option>
            <option value="manufacturer">Manufacturer</option>
          </select>
          <input type="text" placeholder="Filter..." value={filterBy} onChange={e => setFilterBy(e.target.value)}
            style={{ padding: "7px 12px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, outline: "none", background: C.grayBg, fontFamily: "inherit", width: 150 }} />
          {filterBy && <button onClick={() => setFilterBy("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 18, lineHeight: 1 }}>×</button>}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", width: "100%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "110px 120px 130px 110px 1fr 1fr 120px", gap: 12, padding: "10px 20px", background: C.grayBg, borderBottom: `1px solid ${C.border}` }}>
          {["Ordered Date", "Quote Ref.", "PO Number", "Amount", "Company", "Manufacturer", "Status"].map(col => (
            <span key={col} style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>{col}</span>
          ))}
        </div>

        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 280px)" }}>
          {loading ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: C.t3, fontSize: 14 }}>Loading orders...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: C.t3, fontSize: 14 }}>
              {orders.length === 0 ? "No orders yet." : "No orders match your filter."}
            </div>
          ) : filtered.map((o, i) => (
            <div key={o.id}
              onClick={() => setSelected(o)}
              style={{ display: "grid", gridTemplateColumns: "110px 120px 130px 110px 1fr 1fr 120px", gap: 12, padding: "13px 20px", borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", transition: "background 0.15s", alignItems: "center" }}
              onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ fontSize: 12, color: C.t3 }}>{fmtDate(o.submittedDate)}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{o.quoteNumber || "—"}</span>
              <span style={{ fontSize: 12, color: C.t2 }}>{o.poNumber || "—"}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{fmt(o.amount)}</span>
              <span style={{ fontSize: 13, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.company || "—"}</span>
              <span style={{ fontSize: 13, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.manufacturer || "—"}</span>
              <div onClick={e => e.stopPropagation()}>
                <StatusPill status={o.status || "Received"} orderId={o.id} isAdmin={isAdmin} onStatusChange={handleStatusChange} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <OrderDetail
          order={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          onUpdate={(updated) => {
            setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
            setSelected(updated);
          }}
          onOpenQuote={handleOpenQuote}
        />
      )}
      {linkedQuote && (
        <QuoteDetail
          quote={linkedQuote}
          isAdmin={isAdmin}
          user={user}
          onClose={() => setLinkedQuote(null)}
          onStatusChange={() => {}}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
}
